import { actionCreatorFactory } from "typescript-fsa";
import { bindThunkAction, isFailure as isFailureAction } from "typescript-fsa-redux-thunk";
import isEqual from "lodash/isEqual";

import { isClientId } from "../../lib/entities";
import { RootState } from "../../state";
import { actionCreatorFactoryForBootstrap } from "../../lib/bootstrap";
import { isFailure } from "../../common/actions/VisitApi";
import {
  Post,
  Company,
  CompanyValue,
  CompanyPostPin,
  CompanyShowResponse,
  requestToLoadCompanyShow,
  CompanyAboutResponse,
  requestToLoadCompanyAbout,
  CompanyMembersResponse,
  requestToLoadCompanyMembers,
  CompanyPostsResponse,
  requestToLoadCompanyPosts,
  CompanyProjectsResponse,
  requestToLoadCompanyProjects,
  SimilarCompaniesResponse,
  requestToLoadSimilarCompanies,
  LoadPostsResponse,
  requestToLoadPosts,
  loadPinnablePostsResponse,
  requestToLoadPinnablePosts,
  UpdateCompanyResponse,
  requestToUpdateCompany,
  UpdateCompanyValuesResponse,
  requestToUpdateCompanyValues,
  LoadInvisibleCompanyValuesResponse,
  requestToLoadInvisibleCompanyValues,
  requestToCreatePostPin,
  requestToDeletePostPin,
  CreateCompanyPostPayload,
  requestToSendCompanyPost,
} from "../api";
import {
  CompanyMembership,
  CompanyMembershipSection,
  requestToUpdateMembershipSection,
  requestToCreateMembershipSection,
  requestToDeleteMembershipSection,
  requestToUpdateMembershipsInBatch,
} from "../../company_memberships/api";
import { companyValueValidatorCreator, membershipSectionValidator } from "../validation";
import { validateAll, EntityValidationMapError } from "../../lib/entities/validation";

interface DenormalizedSection {
  section?: CompanyMembershipSection;
  memberships: CompanyMembership[];
}

const actionCreator = actionCreatorFactory("COMPANIES");
const bootstrapActionCreator = actionCreatorFactoryForBootstrap();

/**
 * Load companies#show
 */
export const bootstrapCompanyShow = bootstrapActionCreator<CompanyShowResponse>("companies#show");

export const loadCompanyShow = actionCreator.async<RootState, number | string, CompanyShowResponse, Error>(
  "LOAD_COMPANY_SHOW"
);
export const loadCompanyShowWorker = bindThunkAction(loadCompanyShow, async (companyIdOrName, dispatch) => {
  const req = requestToLoadCompanyShow(companyIdOrName);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Load companies#about
 */
export const bootstrapCompanyAbout = bootstrapActionCreator<CompanyAboutResponse>("companies#about");

export const loadCompanyAbout = actionCreator.async<RootState, number | string, CompanyAboutResponse, Error>(
  "LOAD_COMPANY_ABOUT"
);
export const loadCompanyAboutWorker = bindThunkAction(loadCompanyAbout, async (companyIdOrName, dispatch) => {
  const req = requestToLoadCompanyAbout(companyIdOrName);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Load companies#members
 */
export const bootstrapCompanyMembers = bootstrapActionCreator<CompanyMembersResponse>("companies#members");

export const loadCompanyMembers = actionCreator.async<RootState, number | string, CompanyMembersResponse, Error>(
  "LOAD_COMPANY_MEMBERS"
);
export const loadCompanyMembersWorker = bindThunkAction(loadCompanyMembers, async (companyIdOrName, dispatch) => {
  const req = requestToLoadCompanyMembers(companyIdOrName);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Load companies#feed
 */
export const bootstrapCompanyPosts = bootstrapActionCreator<CompanyPostsResponse>("companies#feed");

export const loadCompanyPosts = actionCreator.async<RootState, number | string, CompanyPostsResponse, Error>(
  "LOAD_COMPANY_POSTS"
);
export const loadCompanyPostsWorker = bindThunkAction(loadCompanyPosts, async (companyIdOrName, dispatch) => {
  const req = requestToLoadCompanyPosts(companyIdOrName);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Load companies#projects
 */
export const bootstrapCompanyProjects = bootstrapActionCreator<CompanyProjectsResponse>("companies#projects");

export const loadCompanyProjects = actionCreator.async<RootState, number | string, CompanyProjectsResponse, Error>(
  "LOAD_COMPANY_PROJECTS"
);
export const loadCompanyProjectsWorker = bindThunkAction(loadCompanyProjects, async (companyIdOrName, dispatch) => {
  const req = requestToLoadCompanyProjects(companyIdOrName);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  const json = res.payload.body;
  return json;
});

/**
 * Switch editable mode
 */
export const editModeStarted = actionCreator("EDIT_MODE_STARTED");
export const editModeFinished = actionCreator("EDIT_MODE_FINISHED");

/**
 * Reset current draft
 */
export const draftReset = actionCreator("DRAFT_RESET");

/**
 * Update drafts without saving
 */
export const draftCompanyUpdated = actionCreator<Company>("DRAFT_COMPANY_UPDATED");
export const draftCompanyAvatarUpdated = actionCreator<Company["avatar"]>("DRAFT_COMPANY_AVATAR_UPDATED");
export const draftCompanyBackgroundUpdated = actionCreator<Company["background"]>("DRAFT_COMPANY_BACKGROUND_UPDATED");

export const draftCompanyValuesUpdated = actionCreator<{ companyValues: CompanyValue[] }>(
  "DRAFT_COMPANY_VALUES_UPDATED"
);

export const draftMembershipsChanged = actionCreator<{
  membershipSections: DenormalizedSection[];
  featuredMemberships: CompanyMembership[];
}>("DRAFT_MEMBERSHIPS_CHANGED");

export const draftPostPinUpdated = actionCreator<{ postId: number | null; categoryCd: number }>(
  "DRAFT_POST_PINS_UPDATED"
);

/**
 * Save all drafts.
 * If the dirty field is not true, skip to request.
 */
export const saveDraftAll = actionCreator.async<RootState, void, void, Error>("SAVE_DRAFT_ALL");

export const saveDraftAllWorker = bindThunkAction(saveDraftAll, async (_params, dispatch, getState) => {
  const {
    common: { isDraftCompanyDirty },
    show: { isDraftCompanyValuesDirty },
    members: { isDraftMembershipsDirty },
    posts: { isDraftPostPinsDirty },
  } = getState().companies;
  if (isDraftCompanyDirty) {
    const res = await dispatch(saveDraftCompanyWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftCompanyValuesDirty) {
    const res = await dispatch(saveDraftCompanyValuesWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftMembershipsDirty) {
    const res = await dispatch(saveDraftCompanyMembershipsWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
  if (isDraftPostPinsDirty) {
    const res = await dispatch(saveDraftPostPinsWorker());
    if (isFailureAction(res)) {
      throw res.error;
    }
  }
});

/**
 * Save draft company.
 */
export const saveDraftCompany = actionCreator.async<RootState, void, UpdateCompanyResponse, Error>(
  "SAVE_DRAFT_COMPANY"
);

const saveDraftCompanyWorker = bindThunkAction(saveDraftCompany, async (_params, dispatch, getState) => {
  const {
    common: { company, draftCompany, draftCompanyAvatar, draftCompanyBackground },
  } = getState().companies;
  const { avatar: originalAvatar, background: originalBackground } = company!;
  const avatarNotChanged = originalAvatar && draftCompanyAvatar && originalAvatar.url === draftCompanyAvatar.url;
  const backgroundNotChanged =
    originalBackground && draftCompanyBackground && originalBackground.url === draftCompanyBackground.url;
  const res = await dispatch(
    requestToUpdateCompany({
      company: draftCompany!,
      avatar: avatarNotChanged ? undefined : draftCompanyAvatar,
      background: backgroundNotChanged ? undefined : draftCompanyBackground,
    })
  );
  if (isFailure(res)) {
    throw res.error;
  }
  return res.payload.body;
});

/**
 * Save draft company values.
 */
type SaveDraftCompanyValueError = EntityValidationMapError<CompanyValue> | Error;
export const saveDraftCompanyValues = actionCreator.async<
  RootState,
  void,
  UpdateCompanyValuesResponse,
  SaveDraftCompanyValueError
>("SAVE_DRAFT_COMPANY_VALUES");

const saveDraftCompanyValuesWorker = bindThunkAction(saveDraftCompanyValues, async (_params, dispatch, getState) => {
  const {
    companies: {
      common: { draftCompany },
      show: { draftCompanyValues },
    },
  } = getState();
  const visibleValues = draftCompanyValues.filter(v => v.rank >= 0);
  const maxValueDescLength = draftCompany!.country === "japan" ? 255 : 500;
  const companyValueValidator = companyValueValidatorCreator(maxValueDescLength);
  validateAll(visibleValues, companyValueValidator);
  const res = await dispatch(
    requestToUpdateCompanyValues({ companyId: draftCompany!.id, companyValues: draftCompanyValues })
  );
  if (isFailure(res)) {
    throw res.error;
  }
  return res.payload.body;
});

/**
 * Save draft company memberships
 */
type saveDraftCompanyMembershipsError = EntityValidationMapError<CompanyMembershipSection> | Error;
export const saveDraftCompanyMemberships = actionCreator.async<
  RootState,
  void,
  { sections: CompanyMembershipSection[]; updatedMemberships: CompanyMembership[] },
  saveDraftCompanyMembershipsError
>("SAVE_DRAFT_COMPANY_MEMBERSHIPS");

const saveDraftCompanyMembershipsWorker = bindThunkAction(
  saveDraftCompanyMemberships,
  async (_params, dispatch, getState) => {
    const {
      common: { company },
      members: { sections, memberships, draftMembershipSections, draftFeaturedMemberships },
    } = getState().companies;
    // Validate sctions
    validateAll(draftMembershipSections.map(s => s.section!).filter(Boolean), membershipSectionValidator);
    // Classify CompanyMembershipSections to update, create or delete
    const sectionsToUpdate: CompanyMembershipSection[] = [];
    const sectionsToCreate: CompanyMembershipSection[] = [];
    const sectionIdsToDelete: number[] = [];
    const existingSectionIds: number[] = [];
    draftMembershipSections.forEach((sec, index) => {
      if (!sec.section) {
        return;
      }
      const section = { ...sec.section, display_order: index + 1 };
      if (isClientId(section.id)) {
        sectionsToCreate.push(section);
      } else {
        sectionsToUpdate.push(section);
      }
      existingSectionIds.push(section.id);
    });
    sections.filter(section => !existingSectionIds.includes(section.id)).forEach(section => {
      sectionIdsToDelete.push(section.id);
    });

    const newSections: CompanyMembershipSection[] = [];

    // Update CompanyMembershipSections
    const allUpdateRes = await Promise.all(
      sectionsToUpdate.map(section => {
        return dispatch(requestToUpdateMembershipSection(section));
      })
    );
    allUpdateRes.forEach(res => {
      if (isFailure(res)) {
        throw res.payload.body;
      } else {
        newSections.push(res.payload.body);
      }
    });

    // Create CompanyMembershipSections
    const clientIdsMap: { [key: number]: number } = {}; // client-generated ID => server-generated ID
    const allCreateRes = await Promise.all(
      sectionsToCreate.map(section => {
        return dispatch(requestToCreateMembershipSection({ section, companyId: company!.id }));
      })
    );
    allCreateRes.forEach((res, index) => {
      if (isFailure(res)) {
        throw res.payload.body;
      } else {
        const newSection = res.payload.body;
        newSections.push(newSection);
        const section = sectionsToCreate[index];
        clientIdsMap[section.id] = newSection.id;
      }
    });

    // Delete CompanyMembershipSections
    await Promise.all(
      sectionIdsToDelete.map(id => {
        return dispatch(requestToDeleteMembershipSection(id));
      })
    );

    // Update memberships
    const membershipsToUpdate: CompanyMembership[] = [];
    const featuredIds = draftFeaturedMemberships.map(m => m.id);
    draftMembershipSections.forEach(sec => {
      sec.memberships.forEach((membership, index) => {
        const featuredIndex = featuredIds.indexOf(membership.id);
        const newMembership = {
          ...membership,
          display_order: index + 1,
          featured_display_order: featuredIndex >= 0 ? featuredIndex + 1 : -1,
          section: sec.section ? { id: clientIdsMap[sec.section.id] || sec.section.id } : null,
        };
        // Request to change only for changed membership
        const originalMembership = memberships.find(m => m.id === membership.id);
        if (!isEqual(newMembership, originalMembership)) {
          membershipsToUpdate.push(newMembership);
        }
      });
    });
    const res = await dispatch(requestToUpdateMembershipsInBatch(membershipsToUpdate));
    if (isFailure(res)) {
      throw res.payload.body;
    }
    const updatedMemberships = res.payload.body;
    return { sections: newSections, updatedMemberships };
  }
);

/**
 * Save draft company post pins
 */
export const saveDraftPostPins = actionCreator.async<RootState, null, CompanyPostPin[], Error>("SAVE_DRAFT_POST_PINS");

export const saveDraftPostPinsWorker = bindThunkAction(saveDraftPostPins, async (_params, dispatch, getState) => {
  const { postPins, draftPostPins } = getState().companies.posts;
  const postPinsToCreate: CompanyPostPin[] = [];
  const postPinsToDelete: CompanyPostPin[] = [];
  const newPostPins: CompanyPostPin[] = [];
  draftPostPins.forEach(pin => {
    const originalPin = postPins.find(p => p.category_cd === pin.category_cd);
    if (pin.post) {
      if (!originalPin || !originalPin.post || pin.post.id !== originalPin.post.id) {
        postPinsToCreate.push(pin);
      }
      newPostPins.push(pin);
    } else {
      if (originalPin) {
        postPinsToDelete.push(pin);
      }
    }
  });

  // Delete pins
  const allDeleteRes = await Promise.all(
    postPinsToDelete.map(pin => {
      return dispatch(requestToDeletePostPin(pin.id));
    })
  );
  allDeleteRes.forEach(res => {
    if (isFailure(res)) {
      throw res.payload.body;
    }
  });

  // Create pins
  const allCreateRes = await Promise.all(
    postPinsToCreate.map(pin => {
      return dispatch(requestToCreatePostPin(pin));
    })
  );
  allCreateRes.forEach(res => {
    if (isFailure(res)) {
      throw res.payload.body;
    }
    const newPin = res.payload.body;
    const index = newPostPins.findIndex(pin => pin.category_cd === newPin.category_cd);
    newPostPins[index] = newPin;
  });
  return newPostPins;
});

/**
 * Load similar companies
 */
export const loadSimilarCompanies = actionCreator.async<RootState, number, SimilarCompaniesResponse, Error>(
  "LOAD_SIMILAR_COMPANIES"
);
export const loadSimilarCompaniesWorker = bindThunkAction(loadSimilarCompanies, async (companyId, dispatch) => {
  const req = requestToLoadSimilarCompanies(companyId);
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return res.payload.body;
});

/**
 * Load next posts for infinite scroll
 */
export const loadNextPosts = actionCreator.async<
  RootState,
  { companyId: number },
  { posts: LoadPostsResponse; meta: { isLastPage: boolean } },
  Error
>("LOAD_NEXT_POSTS");

export const loadNextPostsWorker = bindThunkAction(loadNextPosts, async (params, dispatch, getState) => {
  const { currentPage } = getState().companies.posts;
  const { companyId } = params;
  const req = requestToLoadPosts({ companyId, page: currentPage + 1 });
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return {
    posts: res.payload.body,
    meta: { isLastPage: res.payload.headers["X-List-IsLastPage"]! },
  };
});

/**
 * Load pinnable posts
 */
export const loadPinnablePosts = actionCreator.async<
  RootState,
  { companyId: number },
  loadPinnablePostsResponse,
  Error
>("LOAD_PINNABLE_POSTS");

export const loadPinnablePostsWorker = bindThunkAction(loadPinnablePosts, async (params, dispatch) => {
  const req = requestToLoadPinnablePosts({ companyId: params.companyId });
  const res = await dispatch(req);
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return res.payload.body;
});

/**
 * Create company feed post
 */
export const sendCompanyPost = actionCreator.async<RootState, CreateCompanyPostPayload, Post, Error>(
  "SEND_COMPANY_POST"
);
export const sendCompanyPostWorker = bindThunkAction(sendCompanyPost, async (params, dispatch) => {
  const res = await dispatch(requestToSendCompanyPost(params));
  if (isFailure(res)) {
    throw res.payload.body;
  }
  return res.payload.body;
});

/**
 * Load invisible company values
 */
export const loadInvisibleCompanyValues = actionCreator.async<
  RootState,
  Company,
  LoadInvisibleCompanyValuesResponse,
  Error
>("LOAD_INVISIBLE_COMPANY_VALUES");

export const loadInvisibleCompanyValuesWorker = bindThunkAction(
  loadInvisibleCompanyValues,
  async (company, dispatch) => {
    const res = await dispatch(requestToLoadInvisibleCompanyValues({ companyId: company.id }));
    if (isFailure(res)) {
      throw res.error;
    }
    return res.payload.body;
  }
);



// WEBPACK FOOTER //
// ./frontend/src/companies/actions/index.ts